import React from 'react'
import { useForm } from 'react-hook-form'
import { Loader2, Send } from 'lucide-react'
import { refinePodcastScript } from '../../../../api/podcasts'
import { useEditorStore } from '../../state/editor-store'
import type { Podcast } from '../../../../types/podcast'
import { ApiError } from '../../../../api/config'
import Textarea from '../../../../components/ui/textarea'
import { track } from '../../../../lib/analytics/posthog'

type FormValues = { instruction: string }

export const ScriptRefineChat: React.FC<{ podcast?: Podcast; onMinHeightChange?: (min: number) => void; onSubmitSuccess?: () => void }> = ({ podcast, onMinHeightChange, onSubmitSuccess }) => {
  const { register, handleSubmit, reset, watch, formState: { isSubmitting, errors } } = useForm<FormValues>()
  const scriptStatus = useEditorStore((s) => s.scriptJobStatus)
  const setScriptStatus = useEditorStore((s) => s.setScriptJobStatus)
  const setActivity = useEditorStore((s) => s.setActivity)
  const instructionVal = watch('instruction') || ''
  const maxCharacters = 2000

  const lastSummary = podcast?.latestScriptRevisionSummary ?? null

  // const quickSuggestions = React.useMemo(() => [
  //   'Shift the tone to more inspirational storytelling while keeping the facts intact',
  //   'Simplify the explanation of the AI workflow so a non-technical listener can follow along',
  //   'Make the market analysis more concrete with specific numbers or case studies',
  //   'Dial back the metaphors and keep the language straightforward and literal',
  //   'Add richer detail to the product example so the audience can visualize the solution'
  // ], [])

  const suggestionsRef = React.useRef<HTMLDivElement | null>(null)
  const guidanceRef = React.useRef<HTMLDivElement | null>(null)
  const submitRowRef = React.useRef<HTMLDivElement | null>(null)

  const onSubmit = async (values: FormValues) => {
    const instruction = values.instruction?.trim()
    if (!podcast?.id || !instruction) return

    track('editor_chat_submit', {
      characters: instruction.length,
      hasPreviousRevision: Boolean(lastSummary),
    })
    // Optimistic UI: mark as generating immediately; SignalR will drive subsequent states
    setScriptStatus('running')
    setActivity({ phase: 'script', message: 'Applying your updates…', percent: 5, step: 'starting' })
    // Also mirror backend behavior by toggling podcast.isGenerating to true optimistically
    useEditorStore.setState((s) => { if (s.podcast) s.podcast.isGenerating = true })
    try {
      const ack = await refinePodcastScript(podcast.id, { instruction })
      if (ack && (ack as any).message) {
        track('editor_chat_submit_acknowledged', { hasMessage: true })
      }
      // Keep status as 'running'; SignalR events will transition to completed/failed
      reset({ instruction: '' })
      // Notify parent to collapse the chat
      onSubmitSuccess?.()
    } catch (err) {
      const error = err as ApiError
      const friendly = error?.message || 'Could not start update. Please retry.'
      setScriptStatus('failed', friendly)
      setActivity({ phase: 'script', message: friendly, percent: 100, step: 'completed' })
      // Revert optimistic isGenerating
      useEditorStore.setState((s) => { if (s.podcast) s.podcast.isGenerating = false })
      track('editor_chat_submit_failed', {
        message: friendly,
      })
    }
  }

  // const handleSuggestion = (suggestion: string, index: number) => {
  //   if (scriptStatus === 'running') return
  //   setValue('instruction', suggestion, { shouldDirty: true })
  //   setFocus('instruction')
  //   track('editor_chat_suggestion_clicked', {
  //     index,
  //     length: suggestion.length,
  //   })
  // }

  // Measure dynamic parts to compute a realistic minimal height to avoid overflow
  React.useEffect(() => {
    const measure = () => {
      const suggestionsH = suggestionsRef.current?.offsetHeight ?? 0
      const guidanceH = guidanceRef.current?.offsetHeight ?? 0
      const submitH = submitRowRef.current?.offsetHeight ?? 0
      const textareaMin = 72 // matches shared textarea min height
      const paddings = 16 + 8 // outer pt-2/pb-2 + internal gaps
      const MIN = textareaMin + submitH + suggestionsH + guidanceH + paddings
      onMinHeightChange?.(MIN)
    }
    measure()
    const ro = new ResizeObserver(measure)
    if (submitRowRef.current) ro.observe(submitRowRef.current)
    if (suggestionsRef.current) ro.observe(suggestionsRef.current)
    if (guidanceRef.current) ro.observe(guidanceRef.current)
    return () => ro.disconnect()
  }, [instructionVal.length, onMinHeightChange])

  return (
    <div className="h-full flex flex-col" style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0px)' }}>
      {/* Body */}
      <div className="px-4 sm:px-5 pt-2 pb-[50px] sm:pb-2 flex-1 flex flex-col">

        {/* {quickSuggestions.length > 0 && (
          <div ref={suggestionsRef} className="mb-3 hidden sm:block">
            <div className="rounded-2xl bg-white/85 px-3 py-2 shadow-sm ring-1 ring-black/5">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-foreground/55 mb-1">Quick examples</div>
              <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                {quickSuggestions.map((suggestion, index) => (
                  <button
                    key={suggestion}
                    type="button"
                    className="cursor-pointer text-left min-w-fit rounded-lg bg-black/[0.04] px-3 py-2 text-[12px] leading-snug text-foreground/70 transition hover:bg-brand-50 hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => handleSuggestion(suggestion, index)}
                    disabled={scriptStatus === 'running'}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )} */}

        {/* Input composer */}
        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 flex flex-col">
          <div className="flex-1 min-h-[80px] flex flex-col gap-2">
            <div className="flex-1 min-w-0 relative">
              <label htmlFor="refine" className="sr-only">Refine script</label>
              <Textarea
                id="refine"
                rows={3}
                placeholder="Write to improve and update the script: change tone, emphasize specifics, simplify or expand, adjust pacing, etc."
                wrapperClassName="h-full"
                className="resize-none min-h-[72px] h-full pr-12 text-[15px] sm:text-sm"
                {...register('instruction', { required: true, minLength: 4, maxLength: maxCharacters })}
                onKeyDown={(e) => {
                  const key = e.key || ''
                  const isEnter = key === 'Enter' || key === 'NumpadEnter' || (e as any).code === 'Enter'
                  const modifier = (e.metaKey || e.ctrlKey)
                  if (modifier && isEnter) {
                    e.preventDefault()
                    void handleSubmit(onSubmit)()
                  }
                }}
              />
              <button
                type="submit"
                className="absolute bottom-2.5 right-2.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-brand-600 text-white shadow-sm transition hover:bg-brand-700 active:bg-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white disabled:pointer-events-none disabled:opacity-50"
                disabled={isSubmitting || scriptStatus === 'running'}
                aria-label="Send refinement"
              >
                {isSubmitting || scriptStatus === 'running' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
              {errors.instruction && (
                <p className="mt-1 text-xs text-danger-600">Please enter a bit more detail.</p>
              )}
            </div>
            {/* Footer row: counter + submit */}
            <div ref={submitRowRef} className="h-0" aria-hidden="true" />
          </div>
        </form>
      </div>
    </div>
  )
}

export default ScriptRefineChat
